// Sub-state derivation for the smart WhatsApp follow-up feature.
//
// Spec: docs/superpowers/specs/2026-06-04-smart-whatsapp-resume-design.md
//
// PURE FUNCTION — same signature is consumed by:
//   - the admin SmartFollowUpCard (renders the badge + picks the message)
//   - the bulk-relance row in AdminOrdersPage (column label)
//   - the worker /continue/:token redirector (picks the URL)
//
// Keeping this in @/lib (frontend-only) keeps the dependency direction one-
// way: worker imports nothing from web. Worker re-implements the same logic
// against its DB types — both paths are unit-tested against the same fixtures
// in followUpStateMachine.test.ts to prevent drift.

// ─── States, rewritten against the REAL funnel (2026-08-12) ─────────────────
//
// Measured before rewriting: of 49 live orders, 44 were `photos_missing`, 4
// `editor_stuck`, 1 `album_2_missing` — and ZERO were `cover_missing`,
// `mode_not_chosen` or `editor_not_opened`. Those three could not fire at all
// since the funnel changed, yet the machine still carried them, and 92% of
// follow-ups were being logged against a raw order status instead of a state.
//
// Deleted: cover_missing / cover_now_available (the customer no longer picks a
// cover inside the funnel), mode_not_chosen + editor_not_opened (AI layout runs
// on its own now, so neither state is reachable).
//
// Added: the second half of the journey, which had no states at all even though
// it is where the money stalls — preview sent, changes requested, and above all
// APPROVED-BUT-UNPAID (6 orders, 2 408 MAD, chased by nobody).
export const SUB_STATES = {
  /** Order exists, no album session at all — nothing has started. */
  not_started:          'not_started',
  /** Session exists, zero photos. The single biggest state by far. */
  photos_missing:       'photos_missing',
  /** Cover link sent, client has not chosen a design yet. */
  cover_not_chosen:     'cover_not_chosen',
  /** Duo/Trio — a later album is still missing photos. */
  album_2_missing:      'album_2_missing',
  /** Layout exists and the editor was opened, but never finished. */
  editor_stuck:         'editor_stuck',
  /** 3D preview sent, client has not answered. */
  awaiting_preview_feedback: 'awaiting_preview_feedback',
  /** Client asked for changes — WAITING ON US, never a client relance. */
  changes_requested:    'changes_requested',
  /** Preview approved, no advance and no full payment. Where money stalls. */
  awaiting_payment:     'awaiting_payment',
  /** Album submitted by the customer — the « Album soumis » DEPOSIT ask (RIB
   *  inside). ⚠️ No longer derived since 2026-09-29: before approval there is
   *  no money to ask for, and approved-unpaid is `awaiting_payment`. Kept for
   *  history rows, custom templates and a person composing it by hand. */
  submitted:            'submitted',
  /** Submitted, and nothing to ask HER: the 3D preview is ours to send, or the
   *  order is approved and paid and ours to confirm (2026-09-29, #WA-561228). */
  submitted_waiting:    'submitted_waiting',
  printed_not_shipped:  'printed_not_shipped',
  in_transit:           'in_transit',
  delivered:            'delivered',
} as const;

/** States where WE owe the client work — they must alert staff, never message
 *  the client. Nothing surfaced these before, so clients waited in silence. */
export const WAITING_ON_US: ReadonlySet<string> = new Set(['changes_requested', 'submitted_waiting']);
export type SubState = keyof typeof SUB_STATES;

export interface SessionLite {
  id: string;
  album_index: number;
  cover_id: string | null;
  pages: unknown | null;       // jsonb — null when no layout has been saved
  last_saved_at: string | null; // ISO timestamp when editor last touched session
}

export interface SubStateInput {
  /** orders.status — coarse-grained order lifecycle. */
  status: string;
  /** orders.bundle — Solo / Duo / Trio. */
  bundle: 'Solo' | 'Duo' | 'Trio' | string | null;
  /** All sessions for the order, may be empty if customer never started. */
  sessions: SessionLite[];
  /** Photos count per album_index. Map so callers can build it lazily. */
  photoCounts: Map<number, number>;
  /** orders.ameex_status — used to differentiate printed_not_shipped vs in_transit. */
  ameexStatus: string | null;
  /** Has the 3D preview link been minted? (orders.preview_token) — 2026-08-12 */
  previewSent?: boolean;
  /** orders.client_review_status — 'approved' | 'changes_requested' | null. */
  reviewStatus?: string | null;
  /** Advance proof uploaded OR paid in full OR paid_at set. Any of the three
   *  means we have money and must stop asking for it. */
  hasPayment?: boolean;
  /** Does the customer ever CHOOSE a cover? (2026-08-07.) In the simplified
   *  funnel the cover step is gone — the team picks the cover after the order
   *  arrives — so `cover_id` is legitimately NULL on every order forever.
   *
   *  Without this flag the three mirrored copies of this machine would report
   *  EVERY such order as `cover_missing`: every WhatsApp "continue" link would
   *  send the customer to a step that no longer exists, every follow-up message
   *  would tell them to choose a cover, and the admin list would show the whole
   *  book of business as stuck. Defaults to TRUE so the classic funnel is
   *  untouched. */
  coverStepEnabled?: boolean;
}

export interface SubStateResult {
  subState: SubState;
  /** Album the resume link should target. For multi-album orders this is the
   *  album that needs the customer's attention next. */
  lastTouchedAlbum: number;
}

const TOTAL_ALBUMS_BY_BUNDLE: Record<string, number> = {
  Solo: 1,
  Duo: 2,
  Trio: 3,
};

/** Number of albums in a bundle (Solo 1 / Duo 2 / Trio 3). Single frontend
 *  source of truth — used by the funnel derivation AND the acompte = 50×albums
 *  computation (FIX 2). Unknown/null bundles default to 1. Mirrors
 *  BUNDLE_QTY in worker/src/routes/orders.ts. */
export function albumCountForBundle(bundle: SubStateInput['bundle']): number {
  if (!bundle) return 1;
  return TOTAL_ALBUMS_BY_BUNDLE[bundle] ?? 1;
}

function totalAlbumsFor(bundle: SubStateInput['bundle']): number {
  return albumCountForBundle(bundle);
}

function sessionFor(input: SubStateInput, albumIndex: number): SessionLite | undefined {
  return input.sessions.find((s) => s.album_index === albumIndex);
}

/**
 * Compute the sub-state and the album the resume link should target.
 *
 * Decision order matters — we check terminal states (delivered, in_transit)
 * BEFORE upstream-flow states so a delivered order never accidentally shows
 * "cover missing" if a session row is malformed.
 */
export function deriveSubState(input: SubStateInput): SubStateResult {
  const status = input.status;

  // ─── Terminal lifecycle states ──────────────────────────────────────────
  // Status-driven and aligned 1:1 with the pipeline steps so the follow-up
  // template tracks the order status (not AMEEX events). 'shipped' = Expédié;
  // 'in-production' + 'printed' share the production/printed follow-up. Must
  // mirror deriveSubStateInline in worker/src/routes/orders.ts.
  if (status === 'delivered') {
    return { subState: 'delivered', lastTouchedAlbum: 1 };
  }
  if (status === 'shipped') {
    return { subState: 'in_transit', lastTouchedAlbum: 1 };
  }
  if (status === 'in-production' || status === 'printed' || status === 'ready-to-deliver') {
    return { subState: 'printed_not_shipped', lastTouchedAlbum: 1 };
  }
  // 'album-submitted' is the status /submit actually writes; 'in-review' is the
  // admin's review step; legacy 'submitted' is kept for safety. ALL of them mean
  // the customer is done — short-circuit to the deposit follow-up BEFORE the
  // per-album funnel checks, so a fully-submitted Duo/Trio never mis-reads as
  // "next album missing" / "stuck in editor" (regression #1074). Must mirror
  // deriveSubStateInline in worker/src/routes/orders.ts.
  // ─── Post-preview states (2026-08-12) ───────────────────────────────────
  // Checked BEFORE the `submitted` short-circuit: an order that reached the
  // preview is still `album-submitted`, so the old code collapsed all three of
  // these into one generic "submitted" message. That is why 6 approved-but-
  // unpaid orders (2 408 MAD) were never chased — they looked identical to an
  // order that had only just been submitted.
  if (input.reviewStatus === 'changes_requested') {
    // WAITING ON US. Never a client relance — this raises a staff alert.
    return { subState: 'changes_requested', lastTouchedAlbum: 1 };
  }
  if (input.reviewStatus === 'approved' && !input.hasPayment) {
    return { subState: 'awaiting_payment', lastTouchedAlbum: 1 };
  }
  // ⚠️ « PENDING » IS NOT A VERDICT EITHER (2026-09-29). This read
  // `!input.reviewStatus`, and a preview sent stamps 'pending' — so an order
  // waiting on her OK fell through to the deposit ask below. #WA-561228 was
  // sent the RIB while she was still asking to add two photos.
  if (input.previewSent && input.reviewStatus !== 'approved') {
    return { subState: 'awaiting_preview_feedback', lastTouchedAlbum: 1 };
  }

  if (status === 'ready-to-print' || status === 'submitted' || status === 'album-submitted') {
    // ⚠️ NO MONEY ASK BEFORE SHE APPROVED HER ALBUM (owner, 2026-09-29: the
    // system « asked for other infos even she hasn't confirmed the album »).
    // This returned `submitted` — the deposit message, RIB inside — for every
    // submitted order. Approved-and-unpaid is `awaiting_payment` above; every
    // other submitted order has nothing to ask her: the preview is ours to
    // send, or the paid order ours to confirm. A person can still type the
    // deposit message by hand; the card no longer offers it. Must mirror
    // deriveSubStateInline in worker/src/routes/orders.ts.
    return { subState: 'submitted_waiting', lastTouchedAlbum: 1 };
  }

  // ─── Funnel states (status = pending-album or anything not handled above) ─

  const totalAlbums = totalAlbumsFor(input.bundle);

  // A missing session still means "never started"; a missing COVER only means
  // "unfinished" when the funnel actually asks for one.
  const coverStep = input.coverStepEnabled !== false;

  // Sub-state 1: nothing started at all — no session row for album 1.
  // (Was `cover_missing`, which was misleading: with the cover step gone this
  // branch never meant "no cover", it meant "no session". 0 orders ever hit the
  // cover half of the old condition. `cover_not_chosen` is admin-triggered, not
  // derived — see FOLLOW_UP_MESSAGES.)
  const s1 = sessionFor(input, 1);
  if (!s1 || (coverStep && !s1.cover_id)) {
    return { subState: 'not_started', lastTouchedAlbum: 1 };
  }

  // Sub-state 2: photos_missing — album 1 has cover but 0 photos.
  const photos1 = input.photoCounts.get(1) ?? 0;
  if (photos1 === 0) {
    return { subState: 'photos_missing', lastTouchedAlbum: 1 };
  }

  // Sub-state 3: album_2_missing — for Duo/Trio, any later album lacks cover or photos.
  if (totalAlbums >= 2) {
    for (let i = 2; i <= totalAlbums; i++) {
      const s = sessionFor(input, i);
      const photos = input.photoCounts.get(i) ?? 0;
      if (!s || (coverStep && !s.cover_id) || photos === 0) {
        return { subState: 'album_2_missing', lastTouchedAlbum: i };
      }
    }
  }

  // Now all albums have a cover AND at least one photo. Determine if the
  // customer reached the AI/manual layout stage.

  // Sub-state 4: mode_not_chosen — no session has a REAL layout yet. The editor
  // autosaves an EMPTY layout (`{pages: []}`) the moment it mounts the
  // mode-selection modal — before the customer picks AI/Manuel — so a non-null
  // `pages` alone does NOT mean a layout exists. Require actual pages, else an
  // order sitting on the mode-selection screen mis-reads as "editor_stuck".
  // Everything is uploaded — the album is being worked on. `mode_not_chosen`
  // and `editor_not_opened` used to live here; both were unreachable once AI
  // layout started running automatically (0 live orders in either, measured
  // 2026-08-12), so the remaining case is simply "in the editor, not finished".
  const sessionsWithSave = input.sessions
    .filter((s) => s.last_saved_at)
    .sort((a, b) => (b.last_saved_at ?? '').localeCompare(a.last_saved_at ?? ''));
  return {
    subState: 'editor_stuck',
    lastTouchedAlbum: sessionsWithSave[0]?.album_index ?? 1,
  };
}

/**
 * Build the path the resume link should redirect to for a given sub-state.
 * The worker uses this to 302; admin UI uses it to show "Lien: …" in the
 * card preview.
 */
export function resumeUrlPath(result: SubStateResult): string {
  switch (result.subState) {
    case 'editor_stuck':
      return `/editor/${'<id>'}/${result.lastTouchedAlbum}/edit`;
    case 'cover_not_chosen':
      // The client-facing catalogue link, NOT the internal editor route:
      // without ?pick=1 the page bounces straight back out (2026-08-12).
      return `/editor/${'<id>'}/${result.lastTouchedAlbum}/covers?pick=1`;
    case 'not_started':
    case 'photos_missing':
    case 'album_2_missing':
      return `/flow/${'<id>'}`;
    case 'awaiting_preview_feedback':
    case 'changes_requested':
      // The 3D preview link is token-based and minted separately.
      return '';
    case 'awaiting_payment':
    case 'submitted':
    case 'submitted_waiting':
    case 'printed_not_shipped':
    case 'in_transit':
    case 'delivered':
      // Informational — no actionable link. Caller decides whether to show one.
      return '';
  }
}

/** Replace the `<id>` placeholder in resumeUrlPath with the real shopify_order_id. */
export function buildResumeUrl(result: SubStateResult, shopifyOrderId: string, baseUrl: string): string {
  const path = resumeUrlPath(result).replace('<id>', shopifyOrderId);
  if (!path) return '';
  const trimmed = baseUrl.replace(/\/+$/, '');
  return `${trimmed}${path}`;
}
